/**
 * "Your to-do" (plan docs/plans/lms-plan.md 7.1; R1, R3; WP-41): the open
 * assignments sorted by due date, each a row with the course title, why it
 * was assigned in words (source_label: "Required for Clinical Staff", "Sent
 * by Luke on 12 Sep: SOP 4.2 update", "Renewal: expires 3 Nov", 18.16), the
 * due date with its overdue or due-soon state, the estimated minutes, and a
 * Start or Continue button to the player (/lms/courses/:courseId/learn,
 * 18.3; Continue when a bookmark exists). The title opens the course page.
 */
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, ListTodo, Play, RotateCw } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import { primaryBtnCls } from '../../../ui/styles';
import { DueBadge } from '../../components/DueBadge';
import { Section, SectionError, SectionSkeleton } from './Section';
import { coursePath, learnPath } from './homeData';
import type { TodoData } from './homeData';
import type { Loaded } from './useLoad';

export function TodoList({ todo }: { todo: Loaded<TodoData> }) {
  const rows = todo.data?.rows ?? [];
  return (
    <Section
      title="Your to-do"
      icon={ListTodo}
      count={todo.data ? rows.length : null}
      headingId="lms-home-todo-title"
      data-testid="lms-home-todo"
    >
      {todo.loading ? (
        <SectionSkeleton data-testid="lms-home-todo-loading" />
      ) : todo.error ? (
        <SectionError message={todo.error} onRetry={todo.reload} data-testid="lms-home-todo-error" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="Nothing to do right now"
          hint="New trainings assigned to you, and renewals before a training expires, appear here."
        />
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="lms-todo-list">
          {rows.map(({ assignment: a, started }) => {
            const title = a.course?.title ?? 'Course';
            const minutes = a.course?.estimated_minutes;
            return (
              <li
                key={a.id}
                className="flex flex-col gap-2 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:flex-row sm:items-center sm:justify-between"
                data-testid="lms-todo-row"
                data-assignment-id={a.id}
                data-course-id={a.course_id}
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={coursePath(a.course_id)}
                    className="text-sm font-medium text-gray-900 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:hover:text-primary-400"
                    data-testid="lms-todo-title"
                  >
                    {title}
                  </Link>
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400" data-testid="lms-todo-reason">
                    {a.source_label}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {typeof minutes === 'number' ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        {`${minutes} min`}
                      </span>
                    ) : null}
                    {a.mode === 'acknowledge' ? <span>Read the changes and acknowledge</span> : null}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                  <DueBadge dueAt={a.due_at} state={a.due_state} data-testid="lms-todo-due" />
                  <Link
                    to={learnPath(a.course_id)}
                    className={primaryBtnCls}
                    aria-label={`${started ? 'Continue' : 'Start'} ${title}`}
                    data-testid="lms-todo-action"
                  >
                    {started ? <RotateCw className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
                    {started ? 'Continue' : 'Start'}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {todo.data?.hasMore ? (
        <p className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          Showing the first 200 open trainings. The Courses tab lists every course with your status.
        </p>
      ) : null}
    </Section>
  );
}

export default TodoList;
